import type { BaseTariff } from '@/features/Tariffs/types';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';

export enum SORTABLE_ATTRIBUTES {
  BEITRAG = 'beitrag',
  DECKUNGSSUMME = 'deckungssumme',
  ERFUELLUNGSGRAD = 'erfuellungsgrad',
  GESELLSCHAFT = 'gesellschaft',
  LAUFZEIT = 'laufzeit',
  MAX_MOEGLICHER_BEITRAG = 'maxMoeglicherBeitrag',
  MONATLICHE_RENTE = 'monatlicheRente',
  SELBSTBETEILIGUNG = 'selbstbeteiligung',
  TARIFNAME = 'tarifname',
  VERSICHERUNGSSUMME = 'versicherungssumme',
  RATING = 'rating',
}

export enum SORT {
  DESC = 'desc',
  ASC = 'asc',
}

export interface SortSelection {
  key: SORTABLE_ATTRIBUTES;
  order: SORT;
}

export interface SortableDefinition {
  label: string;
  key: string;
  order: SORT.ASC | SORT.DESC;
  handler?: (tariff: BaseTariff) => string;
}

export type Sortables = {
  [key in SORTABLE_ATTRIBUTES]: SortableDefinition;
};

export const sortingGlobals = [
  SORTABLE_ATTRIBUTES.BEITRAG,
  SORTABLE_ATTRIBUTES.ERFUELLUNGSGRAD,
  SORTABLE_ATTRIBUTES.GESELLSCHAFT,
  SORTABLE_ATTRIBUTES.TARIFNAME,
];

export const sortingDefaults = [
  SORTABLE_ATTRIBUTES.ERFUELLUNGSGRAD,
  SORTABLE_ATTRIBUTES.BEITRAG,
];

export const sortables: Sortables = {
  [SORTABLE_ATTRIBUTES.BEITRAG]: {
    label: 'Beitrag',
    key: 'betrag',
    order: SORT.ASC,
  },

  [SORTABLE_ATTRIBUTES.ERFUELLUNGSGRAD]: {
    label: 'Erfüllungsgrad',
    key: 'erfuellungsgrad.wert',
    order: SORT.DESC,
  },

  [SORTABLE_ATTRIBUTES.GESELLSCHAFT]: {
    label: 'Gesellschaft',
    key: 'gesellschaft.name',
    order: SORT.ASC,
    handler: (tariff: BaseTariff) => tariff.gesellschaft.name.toLowerCase(),
  },

  [SORTABLE_ATTRIBUTES.TARIFNAME]: {
    label: 'Tarifname',
    key: 'name',
    order: SORT.ASC,
    handler: (tariff: BaseTariff) => tariff.name.toLowerCase(),
  },

  [SORTABLE_ATTRIBUTES.DECKUNGSSUMME]: {
    label: 'Deckungssumme',
    key: 'deckungssumme',
    order: SORT.DESC,
  },

  [SORTABLE_ATTRIBUTES.SELBSTBETEILIGUNG]: {
    label: 'Selbstbeteiligung',
    key: 'selbstbeteiligung',
    order: SORT.ASC,
  },

  [SORTABLE_ATTRIBUTES.LAUFZEIT]: {
    label: 'Laufzeit',
    key: 'laufzeit',
    order: SORT.ASC,
  },

  [SORTABLE_ATTRIBUTES.MONATLICHE_RENTE]: {
    label: 'monatliche Rente',
    key: 'monatlicheRente',
    order: SORT.DESC,
  },

  [SORTABLE_ATTRIBUTES.MAX_MOEGLICHER_BEITRAG]: {
    label: 'max. möglicher Beitrag',
    key: 'maxMoeglicherBeitrag',
    order: SORT.ASC,
  },

  [SORTABLE_ATTRIBUTES.VERSICHERUNGSSUMME]: {
    label: 'Versicherungssumme',
    key: 'versicherungssumme',
    order: SORT.DESC,
  },

  [SORTABLE_ATTRIBUTES.RATING]: {
    label: 'Bewertung',
    key: 'bewertung',
    order: SORT.DESC,
  },
};

export const createDefaultSortingState = (
  attributes: SORTABLE_ATTRIBUTES[] | undefined = undefined,
): SortSelection[] => {
  let attrs;

  if (attributes === undefined) {
    const configAttributes = getCurrentSpartenConfig().getSortingDefaults;
    if (configAttributes !== undefined) {
      attrs = configAttributes();
    } else {
      attrs = sortingDefaults;
    }
  } else {
    attrs = attributes;
  }

  return attrs.map((key) => ({
    key,
    order: sortables[key].order,
  }));
};
